import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, NotebookPen, Lock, ExternalLink, Check, History } from "lucide-react";
import { NotesHistory } from "@/components/NotesHistory";

export const PropertyNotes = ({ propertyId, propertyName, showLink }) => {
  const [text, setText] = useState(null);
  const [meta, setMeta] = useState({});
  const [state, setState] = useState("saved");
  const [histOpen, setHistOpen] = useState(false);
  const timer = useRef(null);
  const latest = useRef("");

  useEffect(() => {
    setText(null);
    api.get(`/properties/${propertyId}/notes`).then((r) => { setText(r.data.content); latest.current = r.data.content; setMeta(r.data); setState("saved"); })
      .catch((e) => { setText(""); toast.error(formatApiErrorDetail(e.response?.data?.detail)); });
    return () => clearTimeout(timer.current);
  }, [propertyId]);

  const save = async () => {
    clearTimeout(timer.current);
    setState("saving");
    try {
      const r = await api.put(`/properties/${propertyId}/notes`, { content: latest.current });
      setMeta(r.data); setState("saved");
    } catch (e) { setState("error"); toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const onChange = (v) => {
    setText(v); latest.current = v; setState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(save, 1500);
  };

  return (
    <Card className="rounded-none border p-5" data-testid="property-notes">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 font-heading text-lg font-bold"><NotebookPen className="h-5 w-5 text-primary" /> Notes{propertyName ? ` — ${propertyName}` : ""}</div>
          <p className="flex items-center gap-1 text-xs text-muted-foreground"><Lock className="h-3 w-3" /> Stored encrypted · visible to Admin, Regional, Accountant &amp; this property's GM</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground" data-testid="property-notes-status">
            {state === "saving" ? "Saving…" : state === "dirty" ? "Unsaved changes" : state === "error" ? "Not saved" : meta.updated_at ? <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-emerald-600" /> Saved · {meta.updated_by} · {new Date(meta.updated_at).toLocaleString()}</span> : ""}
          </span>
          <Button size="sm" variant="outline" onClick={save} disabled={state === "saving" || text === null} data-testid="property-notes-save">Save</Button>
          <Button size="sm" variant="ghost" onClick={() => setHistOpen(true)} data-testid="property-notes-history"><History className="mr-1 h-3.5 w-3.5" /> History</Button>
          {showLink && <Button size="sm" variant="ghost" asChild><Link to={`/properties/${propertyId}?tab=notes`} data-testid="property-notes-open-tab"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Property Notes tab</Link></Button>}
        </div>
      </div>
      {text === null ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <Textarea value={text} onChange={(e) => onChange(e.target.value)} rows={18} spellCheck={false}
          placeholder={"e.g.\nSales tax portal: https://...\nUsername: ...\nPassword: ...\n\nUtility account #: ..."}
          className="font-mono text-sm leading-relaxed" data-testid="property-notes-text" />
      )}
      <NotesHistory propertyId={propertyId} open={histOpen} onClose={() => setHistOpen(false)}
        onRestore={(c) => { onChange(c); toast.success("Version restored — saving…"); }} />
    </Card>
  );
};
